/**
 * Helpers for records that can belong to many divisions.
 *
 * Multi-division records (materials, equipment, contacts, form templates) carry a
 * `divisionIds` array. Filtering one of them against a division filter is a set
 * intersection: the record is visible as long as it lives in at least one of the
 * selected divisions.
 *
 */

export default {};

/**
 * True when `record` lives in at least one of the divisions in `divisionSet`.
 * @param {object} record record carrying a `divisionIds` array
 * @param {Set<string>} divisionSet the divisions being filtered on
 * @returns {boolean}
 */
export const isInDivisions = (record, divisionSet) => (
  !!divisionSet && !!record?.divisionIds?.some((divisionId) => divisionSet.has(divisionId))
);

/**
 * Filters an array of multi-division records down to those in `divisionSet`.
 * @param {Array<object>} records
 * @param {Set<string>} divisionSet
 * @returns {Array<object>}
 */
export const filterByDivisions = (records, divisionSet) => (
  (records ?? []).filter((record) => isInDivisions(record, divisionSet))
);

/**
 * Same as `filterByDivisions`, but takes and returns an id keyed map.
 * @param {object} recordMap id keyed map of multi-division records
 * @param {Set<string>} divisionSet
 * @returns {object} id keyed map
 */
export const filterMapByDivisions = (recordMap, divisionSet) => (
  Object.values(recordMap ?? {}).reduce((acc, record) => {
    if (isInDivisions(record, divisionSet)) acc[record.id] = record;
    return acc;
  }, {})
);

/**
 * Filters an array of single-division records (scalar `divisionId`) down to `divisionSet`.
 * @param {Array<object>} records
 * @param {Set<string>} divisionSet
 * @returns {Array<object>}
 */
export const filterByDivisionId = (records, divisionSet) => (
  (records ?? []).filter((record) => !!divisionSet && divisionSet.has(record?.divisionId))
);

/**
 * The division ids in `childDivisionIds` that `parentDivisionIds` does not contain.
 * @param {Array<string>} childDivisionIds
 * @param {Array<string>} parentDivisionIds
 * @returns {Array<string>}
 */
export const getMissingDivisionIds = (childDivisionIds = [], parentDivisionIds = []) => {
  const parentSet = new Set(parentDivisionIds);
  return childDivisionIds.filter((divisionId) => !parentSet.has(divisionId));
};

/**
 * True when every division in `childDivisionIds` is also in `parentDivisionIds`.
 * @param {Array<string>} childDivisionIds
 * @param {Array<string>} parentDivisionIds
 * @returns {boolean}
 */
export const isDivisionSubset = (childDivisionIds = [], parentDivisionIds = []) => (
  getMissingDivisionIds(childDivisionIds, parentDivisionIds).length === 0
);

/**
 * True when both lists hold the same divisions, regardless of order.
 * @param {Array<string>} divisionIdsA
 * @param {Array<string>} divisionIdsB
 * @returns {boolean}
 */
export const haveSameDivisions = (divisionIdsA = [], divisionIdsB = []) => (
  isDivisionSubset(divisionIdsA, divisionIdsB)
  && isDivisionSubset(divisionIdsB, divisionIdsA)
);

/**
 * Resolves division ids to their display names, dropping any that are unknown.
 * @param {Array<string>} divisionIds
 * @param {object} divisionMap id keyed map of divisions
 * @returns {Array<string>}
 */
export const getDivisionNames = (divisionIds = [], divisionMap = {}) => (
  divisionIds.map((divisionId) => divisionMap[divisionId]?.name).filter(Boolean)
);

/**
 * Normalizes whatever a DivisionSelector hands back into an array of ids.
 * The selector emits a scalar in single mode and an array in multiple mode.
 * @param {string|Array<string>} value
 * @returns {Array<string>}
 */
export const toDivisionIds = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};
